import { useState, useRef, useEffect } from 'react';
import { Button } from '../common/Button';
import { useCreateTask } from '../../hooks/useCreateTask';
import { useProjectSlaTiers } from '../../hooks/useProjectSlaTiers';
import { SLA_TASK_TYPE_ID } from '../../constants/typeIds';
import { uploadTaskAttachment } from '../../services/api/taskAttachments';
import { attachmentResultFromError } from '../../hooks/useCreateComment';
import { useProjectTaskLabels } from '../../hooks/useTaskLabels';
import { chipTextColor } from '../../utils/format';

// MKS-202 — same policy the portal New-request dropzone enforces
// (NewRequestForm.tsx): 10 files per task, 25 MiB per file, both mirrored from
// the backend caps so an oversized file never costs a round trip.
const MAX_ATTACHMENTS = 10;
const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

interface StagedFile {
  uid: number;
  file: File;
  /** Flagged client-side, before any network call, when file.size exceeds the cap. */
  oversized: boolean;
  /** Server-reported reason, set after this file's upload came back as a failure. */
  errorMessage?: string;
  /** True once this file's upload is confirmed to have succeeded. */
  sent?: boolean;
}

let nextUid = 1;

export interface AddTaskInlineFormProps {
  projectId: string;
  taskStateId: number;
  audience: 'internal' | 'external';
  milestoneId?: number | null; // Phase 31 D-12 — null = "No milestone" row; undefined = no preference
  /** Phase 49 Plan 49-05 — gates the tier select (RC-01). Never read via task.project.taskTypeId;
   * always drilled explicitly from KanbanSwimlane → KanbanSwimlaneCell → AddTaskInlineForm. */
  projectTaskTypeId?: number | null;
  onError?: (message: string) => void;
}

/**
 * Phase 24 Plan 24-08 — TASK-05 inline task-create form at column bottom.
 * Renders nothing for external audience (defense in depth — backend also
 * rejects external lifecycle attempts).
 *
 * Collapsed: "+ Add task" link-style button.
 * Expanded: name <input> (required, autoFocus) + description <textarea>
 *           (optional) + tier <select> (SLA projects only) + Save / Cancel.
 *
 * Phase 49 Plan 49-05 — RC-01 gate: when projectTaskTypeId === SLA_TASK_TYPE_ID,
 * a tier select is shown (required). Save button disabled until tier is picked.
 *
 * MKS-202 — the form also stages files and uploads them to the task it has
 * just created. Order matters: the attachment endpoint is task-scoped
 * (POST /api/tasks/:taskId/attachments), so there is nothing to upload to
 * until create resolves — the same create-then-upload sequence
 * `useCreatePortalRequest.ts` runs for the portal New-request panel. An upload
 * failure never un-creates the task, so on a partial failure the form STAYS
 * open naming the failed files, and `createdId` turns the primary control into
 * "Done" so a second press can never create a duplicate task.
 *
 * MKS-202 — the form also picks from the project's existing labels and sends
 * them as `labelIds` on the create call.
 * Deliberately NOT a PUT /tasks/:id/labels after create: an independent issue
 * reporter (an internal non-member on an open board) may create a task but may
 * not relabel one, so the labels ride inside the create they are already
 * allowed to make. That is the whole of the widening — see
 * `requireProjectReadableByProject` in `backend/src/api/middleware/rbac.ts`.
 *
 * Pitfall 2 — stopPropagation on click + keyDown so events do not bubble
 * into the column's useDroppable drop zone (TaskCard pattern, lines 36-43).
 */
export function AddTaskInlineForm({
  projectId,
  taskStateId,
  audience,
  milestoneId,
  projectTaskTypeId,
  onError,
}: AddTaskInlineFormProps) {
  const [expanded, setExpanded] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [budget, setBudget] = useState('');
  const [hardLimit, setHardLimit] = useState(false);
  const [plannedHours, setPlannedHours] = useState('');
  const [tierId, setTierId] = useState<number | null>(null);
  // Quick 260825-hxx — server/local submit errors, shown inline beside the
  // title field (this form is mounted without an `onError` prop today, so
  // the mutation error was previously discarded entirely).
  const [submitError, setSubmitError] = useState<string | null>(null);
  // MKS-202 — staged attachments, plus the id of the task this form has
  // already created (set only when an upload failed and the form stayed open).
  const [staged, setStaged] = useState<StagedFile[]>([]);
  const [createdId, setCreatedId] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  // MKS-202 — labels picked for the task about to be created.
  const [labelIds, setLabelIds] = useState<number[]>([]);
  const nameRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const createMutation = useCreateTask();

  // MKS-202 — only fetch once the panel is actually open: a board renders one
  // of these per column, and a collapsed "+ Add task" needs no label list.
  // The query key is per-project, so the open columns share one request.
  const labelsQuery = useProjectTaskLabels(expanded ? Number(projectId) : undefined);
  const labelOptions = labelsQuery.data ?? [];

  const isSlaProject = projectTaskTypeId === SLA_TASK_TYPE_ID;
  const tiersQuery = useProjectSlaTiers(isSlaProject ? Number(projectId) : undefined);
  const sortedTiers = (tiersQuery.data?.data ?? []).slice().sort((a, b) => a.orderIndex - b.orderIndex);

  // Focus name input when expanded
  useEffect(() => {
    if (expanded) nameRef.current?.focus();
  }, [expanded]);

  // External actors never see this form
  if (audience !== 'internal') return null;

  function reset() {
    setName('');
    setDescription('');
    setBudget('');
    setHardLimit(false);
    setPlannedHours('');
    setTierId(null);
    setSubmitError(null);
    setStaged([]);
    setCreatedId(null);
    setLabelIds([]);
    setExpanded(false);
  }

  function toggleLabel(labelId: number) {
    setLabelIds((prev) =>
      prev.includes(labelId) ? prev.filter((id) => id !== labelId) : [...prev, labelId],
    );
  }

  // MKS-202 — append picked files, honouring the 10-file room and flagging an
  // oversized one here rather than paying for a doomed upload.
  function stageFiles(picked: File[]) {
    setStaged((prev) => {
      const room = MAX_ATTACHMENTS - prev.length;
      if (room <= 0) return prev;
      return [
        ...prev,
        ...picked.slice(0, room).map((file) => ({
          uid: nextUid++,
          file,
          oversized: file.size > MAX_ATTACHMENT_BYTES,
        })),
      ];
    });
  }

  function removeFile(uid: number) {
    setStaged((prev) => prev.filter((f) => f.uid !== uid));
  }

  /**
   * Upload the staged files one at a time to the freshly created task.
   * Resolves to true when every file landed — the caller resets the form only
   * then, so a partial failure keeps the chips (and their reasons) on screen.
   */
  async function uploadStagedFiles(taskId: number, files: StagedFile[]): Promise<boolean> {
    let allSent = true;
    for (const entry of files) {
      try {
        await uploadTaskAttachment(taskId, entry.file);
        setStaged((prev) =>
          prev.map((f) => (f.uid === entry.uid ? { ...f, sent: true, errorMessage: undefined } : f)),
        );
      } catch (err) {
        allSent = false;
        const result = attachmentResultFromError(entry.file.name, err);
        setStaged((prev) =>
          prev.map((f) =>
            f.uid === entry.uid
              ? { ...f, sent: false, errorMessage: result.message ?? 'Upload failed' }
              : f,
          ),
        );
      }
    }
    return allSent;
  }

  function parseOptionalNumber(raw: string): number | null | 'invalid' {
    const trimmed = raw.trim();
    if (trimmed === '') return null;
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n < 0) return 'invalid';
    return n;
  }

  function handleExpand(e: React.MouseEvent) {
    e.stopPropagation();
    setExpanded(true);
  }

  function handleCancel(e: React.MouseEvent) {
    e.stopPropagation();
    reset();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    // MKS-202 — the task already exists; a prior submit created it and only
    // the uploads failed. Pressing the control again closes the form, it
    // never creates a second task.
    if (createdId !== null) {
      reset();
      return;
    }
    setSubmitError(null);
    const trimmedName = name.trim();
    if (trimmedName.length === 0 || trimmedName.length > 500) return;
    const budgetParsed = parseOptionalNumber(budget);
    const plannedParsed = parseOptionalNumber(plannedHours);
    if (budgetParsed === 'invalid' || plannedParsed === 'invalid') {
      const message = 'Budget and planned hours must be non-negative numbers.';
      setSubmitError(message);
      if (onError) onError(message);
      return;
    }
    // MKS-202 — refuse the submit rather than silently dropping a file the
    // backend would reject anyway; the chip already names which one.
    if (staged.some((f) => f.oversized)) {
      const message = `Remove the attachments over ${MAX_ATTACHMENT_BYTES / (1024 * 1024)} MB before saving.`;
      setSubmitError(message);
      if (onError) onError(message);
      return;
    }
    // Phase 49 Plan 49-05 — RC-01 frontend gate
    if (isSlaProject && tierId == null) {
      const message = 'Priority tier is required for SLA Task projects.';
      setSubmitError(message);
      if (onError) onError(message);
      return;
    }
    createMutation.mutate(
      {
        projectId,
        taskStateId,
        // Phase 31 D-12 — only thread the field when the parent passed a value
        // (undefined → omit so the existing `milestoneId: null` AddTaskInlineForm
        // test fixture body keeps its current shape).
        ...(milestoneId !== undefined ? { milestoneId } : {}),
        name: trimmedName,
        description: description.trim() === '' ? null : description.trim(),
        budget: budgetParsed,
        hardLimit,
        plannedHours: plannedParsed,
        // Phase 49 Plan 49-05 — include tierId + tierData for optimistic badge render
        ...(isSlaProject && tierId != null ? { tierId } : {}),
        ...(isSlaProject && tierId != null
          ? { tierData: sortedTiers.find((t) => t.id === tierId) ?? null }
          : {}),
        // MKS-202 — labelData feeds the optimistic card's chips, exactly as
        // tierData feeds its tier badge.
        ...(labelIds.length > 0
          ? {
              labelIds,
              labelData: labelOptions.filter((l) => labelIds.includes(l.id)),
            }
          : {}),
      },
      {
        onSuccess: (task) => {
          // MKS-202 — nothing staged: unchanged behaviour.
          const toUpload = staged.filter((f) => !f.oversized);
          if (toUpload.length === 0) {
            reset();
            return;
          }
          setCreatedId(task.id);
          setUploading(true);
          void uploadStagedFiles(task.id, toUpload).then((allSent) => {
            setUploading(false);
            if (allSent) {
              reset();
              return;
            }
            const message =
              'Task created, but some attachments failed to upload. Press Done to close.';
            setSubmitError(message);
            if (onError) onError(message);
          });
        },
        onError: (err) => {
          const message = err.message ?? 'Server error';
          setSubmitError(message);
          if (onError) onError(message);
        },
      },
    );
  }

  // Stop bubbling on the form root so any keypress / click inside doesn't
  // hit the column-level useDroppable handlers (Pitfall 2).
  function handleStopPropagation(e: React.SyntheticEvent) {
    e.stopPropagation();
  }

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={handleExpand}
        className="w-full text-left text-sm font-medium text-gray-500 hover:text-gray-700 px-2 py-2 rounded-lg hover:bg-gray-100 transition-colors"
      >
        + Add task
      </button>
    );
  }

  const trimmedName = name.trim();
  // MKS-202 — once the task exists the control only closes the form, so it
  // stays pressable no matter what the (now disabled) fields say.
  const canSave =
    createdId !== null ||
    (trimmedName.length > 0 &&
      trimmedName.length <= 500 &&
      !createMutation.isPending &&
      !uploading &&
      (!isSlaProject || tierId != null)); // Phase 49 Plan 49-05 — RC-01 gate

  return (
    <form
      onSubmit={handleSubmit}
      onClick={handleStopPropagation}
      onKeyDown={handleStopPropagation}
      className="rounded-lg border border-gray-200 bg-white p-2 space-y-2 shadow-sm"
    >
      <input
        ref={nameRef}
        type="text"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          setSubmitError(null);
        }}
        placeholder="Task name"
        maxLength={500}
        className="w-full text-sm font-medium text-gray-900 px-2 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400"
        aria-label="Task name"
      />
      {submitError && (
        <p className="text-sm text-red-700" role="alert">
          {submitError}
        </p>
      )}
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Description (optional)"
        rows={2}
        className="w-full text-sm font-medium text-gray-700 px-2 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
        aria-label="Task description"
      />
      <div className="grid grid-cols-2 gap-2">
        <input
          type="number"
          inputMode="decimal"
          min={0}
          step="0.01"
          value={budget}
          onChange={(e) => setBudget(e.target.value)}
          placeholder="Budget"
          className="w-full text-sm font-medium text-gray-700 px-2 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400"
          aria-label="Budget"
        />
        <input
          type="number"
          inputMode="decimal"
          min={0}
          step="0.01"
          value={plannedHours}
          onChange={(e) => setPlannedHours(e.target.value)}
          placeholder="Planned hours"
          className="w-full text-sm font-medium text-gray-700 px-2 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400"
          aria-label="Planned hours"
        />
      </div>
      <label className="flex items-center gap-2 text-xs font-medium text-gray-600">
        <input
          type="checkbox"
          checked={hardLimit}
          onChange={(e) => setHardLimit(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-primary-500 focus:ring-primary-400"
          aria-label="Hard limit"
        />
        Hard limit
      </label>
      {/* Phase 49 Plan 49-05 — RC-01 tier select (SLA projects only) */}
      {isSlaProject && (
        <div>
          <label
            htmlFor={`task-inline-tier-${taskStateId}`}
            className="block text-xs font-medium text-gray-700 mb-1"
          >
            Priority tier <span className="text-red-500">*</span>
          </label>
          <select
            id={`task-inline-tier-${taskStateId}`}
            value={tierId ?? ''}
            onChange={(e) => setTierId(e.target.value ? Number(e.target.value) : null)}
            className="w-full text-sm font-medium text-gray-700 px-2 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400"
            aria-label="Priority tier"
            data-testid="add-task-tier-select"
          >
            <option value="">— select tier —</option>
            {sortedTiers.map((t) => (
              <option key={t.id} value={t.id}>
                P{t.orderIndex} — {t.name}
              </option>
            ))}
          </select>
          {tierId == null && (
            <p className="mt-1 text-xs text-red-500">Required for SLA Task projects</p>
          )}
        </div>
      )}
      {/* MKS-202 — task type. Toggleable chips rather than TaskLabelPicker's
       * popover: that component assigns labels to an EXISTING task through
       * PUT /tasks/:id/labels, and there is no task yet here. Picking only —
       * creating a new label stays a project-PM action in the slide-over. */}
      {labelOptions.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-medium text-gray-600">Labels</p>
          <div className="flex flex-wrap gap-1" role="group" aria-label="Task labels">
            {labelOptions.map((label) => {
              const selected = labelIds.includes(label.id);
              return (
                <button
                  key={label.id}
                  type="button"
                  onClick={() => toggleLabel(label.id)}
                  disabled={createdId !== null}
                  aria-pressed={selected}
                  data-testid={`add-task-label-${label.id}`}
                  style={
                    selected
                      ? { backgroundColor: label.color, color: chipTextColor(label.color) }
                      : undefined
                  }
                  className={`rounded-full px-2 py-0.5 text-[10px] font-medium leading-4 disabled:opacity-50 ${
                    selected ? '' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {label.name}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {/* MKS-202 — attachments. A plain file input, not the portal's dropzone:
       * a kanban column is far too narrow for a drop target. */}
      <div>
        <input
          ref={fileRef}
          type="file"
          multiple
          className="hidden"
          aria-label="Attach files"
          data-testid="add-task-attachment-input"
          onChange={(e) => {
            stageFiles(Array.from(e.target.files ?? []));
            setSubmitError(null);
            // Clear the input so re-picking the same file still fires onChange.
            e.target.value = '';
          }}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={createdId !== null || staged.length >= MAX_ATTACHMENTS}
          className="text-xs font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50"
        >
          + Attach files
        </button>
        {staged.length > 0 && (
          <ul className="mt-1 space-y-1">
            {staged.map(({ uid, file, oversized, errorMessage: fileError, sent }) => {
              const isError = oversized || !!fileError;
              return (
                <li
                  key={uid}
                  className={`flex items-start justify-between gap-2 rounded px-2 py-1 text-xs ${
                    isError ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-700'
                  }`}
                >
                  <span className="flex flex-col min-w-0">
                    <span className="truncate">{file.name}</span>
                    {oversized && <span className="text-red-600">Over the 25 MB per-file cap</span>}
                    {fileError && !oversized && <span className="text-red-600">{fileError}</span>}
                    {sent && !isError && <span className="text-gray-400">Sent</span>}
                  </span>
                  {createdId === null && (
                    <button
                      type="button"
                      onClick={() => removeFile(uid)}
                      className="shrink-0 text-gray-400 hover:text-gray-600"
                      aria-label={`Remove ${file.name}`}
                    >
                      ×
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div className="flex justify-end gap-2">
        {/* MKS-202 — no "Cancel" once the task exists: it would read as an
         * undo, and nothing here can un-create it. */}
        {createdId === null && (
          <Button type="button" variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          disabled={!canSave}
          isLoading={createMutation.isPending || uploading}
        >
          {createdId !== null ? 'Done' : 'Save'}
        </Button>
      </div>
    </form>
  );
}
